import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { AssetsService } from '../../api/assets.service';
import { BlobStorageService } from '../services/blob-storage.service';

@Component({
	selector: 'of-entity-image-field',
	standalone: true,
	imports: [CommonModule],
	styleUrls: ['./entity-image-field.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => EntityImageFieldComponent),
			multi: true,
		},
	],
	template: `
		<div class="entity-image-field">
			<input #fileInput type="file" class="hidden-input" [accept]="accept" (change)="onFileSelected($event)" [disabled]="disabled || uploading" />

			<div class="image-preview" [class.is-loading]="uploading" (click)="openPreview()" [class.is-clickable]="!!previewUrl">
				<ng-container *ngIf="previewUrl; else noImg">
					<img [src]="previewUrl" [alt]="label" />
				</ng-container>
				<ng-template #noImg>
					<span class="muted">No image</span>
				</ng-template>
			</div>

			<div class="upload-controls">
				<button type="button" class="btn-outline slim" (click)="fileInput.click()" [disabled]="disabled || uploading">
					{{ uploading ? 'Uploading...' : 'Upload' }}
				</button>
				<button type="button" class="btn-outline slim" (click)="openPreview()" [disabled]="!previewUrl">
					Preview
				</button>
			</div>

			<div class="form-alert error" *ngIf="error">{{ error }}</div>
		</div>

		<div *ngIf="showPreview" class="modal-overlay" (click)="closePreview()">
			<div class="modal-card image-preview-modal" (click)="$event.stopPropagation()">
				<div class="modal-header">
					<div>
						<p class="kicker">{{ modalKicker }}</p>
						<h3>{{ modalTitle }}</h3>
					</div>
					<button type="button" class="btn-icon" (click)="closePreview()" [attr.aria-label]="closeLabel">&times;</button>
				</div>
				<div class="modal-body">
					<ng-container *ngIf="previewUrl; else noPreview">
						<img class="image-preview-modal__img" [src]="previewUrl" [alt]="label" />
					</ng-container>
					<ng-template #noPreview>
						<p class="support-text">No image to preview.</p>
					</ng-template>
				</div>
				<div class="modal-actions" style="justify-content: flex-end;">
					<button type="button" class="btn-outline" (click)="closePreview()">Close</button>
				</div>
			</div>
		</div>
	`,
})
export class EntityImageFieldComponent implements ControlValueAccessor {
	@Input({ required: true }) worldId!: string;

	@Input() label = 'Image';
	@Input() accept = 'image/*';
	@Input() assetType = 'world-image';
	@Input() assetPurpose: string | null = 'world-image';
	@Input() blobPathPrefix: string | null = null;

	@Input() modalKicker = 'Entity';
	@Input() modalTitle = 'Image preview';
	@Input() closeLabel = 'Close image preview dialog';

	@Output() uploadingChange = new EventEmitter<boolean>();
	@Output() errorChange = new EventEmitter<string | null>();

	disabled = false;
	uploading = false;
	error: string | null = null;
	showPreview = false;
	previewUrl: string | null = null;

	private value: string = '';
	private onChange: (value: string) => void = () => undefined;
	private onTouched: () => void = () => undefined;

	constructor(private readonly assets: AssetsService, private readonly blobStorage: BlobStorageService) {}

	writeValue(value: string | null): void {
		this.value = value ?? '';
		void this.resolvePreviewFromValue();
	}

	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	openPreview() {
		if (!this.previewUrl) return;
		this.showPreview = true;
	}

	closePreview() {
		this.showPreview = false;
	}

	async onFileSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (!file || this.disabled || this.uploading) return;

		this.setError(null);
		this.setUploading(true);

		try {
			const prefix = this.blobPathPrefix ?? `worlds/${this.worldId}/images`;
			const safeName = file.name.replace(/[\\/]/g, '_');
			const blob = await this.blobStorage.uploadFile(file, `${prefix}/${safeName}`);
			const asset = await firstValueFrom(
				this.assets.create(this.worldId, {
					type: this.assetType,
					purpose: this.assetPurpose,
					name: file.name,
					url: blob.url,
					mimeType: file.type || 'application/octet-stream',
				})
			);

			this.value = asset.asset.id;
			this.previewUrl = blob.sasUrl;
			this.onChange(this.value);
			this.onTouched();
		} catch (err) {
			this.setError(err instanceof Error ? err.message : 'Unable to upload image');
		} finally {
			this.setUploading(false);
			input.value = '';
		}
	}

	private setUploading(next: boolean) {
		this.uploading = next;
		this.uploadingChange.emit(next);
	}

	private setError(message: string | null) {
		this.error = message;
		this.errorChange.emit(message);
	}

	private async resolvePreviewFromValue() {
		const assetId = this.value?.trim();
		if (!assetId) {
			this.previewUrl = null;
			return;
		}

		// Allow the value to be either an asset id or a direct URL.
		if (assetId.startsWith('http')) {
			this.previewUrl = await this.blobStorage.withSas(assetId);
			return;
		}

		try {
			const res = await firstValueFrom(this.assets.getById(this.worldId, assetId));
			this.previewUrl = res?.asset?.url ? await this.blobStorage.withSas(res.asset.url) : null;
		} catch {
			// Keep silent; consumers can still save/edit without a preview.
			this.previewUrl = null;
		}
	}
}
